import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { 
  CalendarIcon, 
  UserGroupIcon, 
  MapPinIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  TicketIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowDownTrayIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ErrorMessage from '../../../components/common/ErrorMessage';

const BookingDetail = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  // Fetch booking details
  const fetchBookingDetails = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const api = axios.create({
        baseURL: apiUrl
      });
      const response = await api.get(`/api/v1/bookings/${bookingId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Log the response for debugging
      console.log('Booking API response:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching booking details:', error);
      throw error;
    }
  };
  
  // Booking details query
  const { 
    data: booking, 
    isLoading, 
    error,
    refetch 
  } = useQuery(['booking', bookingId], fetchBookingDetails, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Cancel booking mutation
  const cancelBookingMutation = useMutation(
    async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const api = axios.create({
          baseURL: apiUrl
        });
        const response = await api.patch(`/api/v1/bookings/${bookingId}/cancel`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error cancelling booking:', error);
        throw error;
      }
    },
    {
      onSuccess: () => {
        refetch();
        setShowCancelModal(false);
      }
    }
  );
  
  // Fallback data for development/testing
  const fallbackBooking = {
    id: parseInt(bookingId),
    tourId: 1,
    tourTitle: 'Savannah Safari Experience',
    tourImage: 'https://source.unsplash.com/random/800x600?safari,wildlife&sig=1',
    tourLocation: 'Savannah National Reserve',
    tourDescription: 'Experience the breathtaking beauty of our most famous wildlife reserve. Witness the incredible wildlife and stunning landscapes on this unforgettable safari adventure.',
    bookingDate: '2025-06-15',
    numTravelers: 2,
    totalPrice: 2598,
    status: 'confirmed',
    paymentStatus: 'paid',
    paymentMethod: 'credit_card',
    createdAt: '2025-03-10T14:30:00Z',
    contactInfo: {
      name: 'John Doe',
      email: 'john.doe@example.com',
      phone: '+1234567890'
    },
    specialRequirements: 'Vegetarian meals required for one traveler. Would prefer a room with a view if possible.',
    itinerary: [
      {
        day: 1,
        title: 'Arrival and Welcome',
        description: 'Arrival at International Airport, transfer to your hotel in Safari City. Welcome dinner and briefing about the safari.'
      },
      {
        day: 2,
        title: 'Journey to Wildlife Reserve',
        description: 'After breakfast, drive to the Savannah National Reserve. Afternoon game drive to spot the Big Five.'
      },
      {
        day: 3,
        title: 'Full Day Safari',
        description: 'Full day game drive in the Savannah National Reserve with picnic lunch. Opportunity to witness the incredible wildlife including lions, elephants, and giraffes.'
      },
      {
        day: 4,
        title: 'Wildlife Reserve Visit',
        description: 'Morning game drive followed by a visit to a traditional village. Learn about their culture and traditions.'
      },
      {
        day: 5,
        title: 'Return to Safari City',
        description: 'Final morning game drive, then return to Safari City. Farewell dinner and departure.'
      }
    ]
  };
  
  // Process the API response data
  const processBookingData = (apiResponse) => {
    if (!apiResponse || !apiResponse.data) return fallbackBooking;
    
    const bookingData = apiResponse.data;
    const tourData = bookingData.tour || {};
    
    return {
      id: bookingData.id,
      booking_id: bookingData.id,
      booking_number: bookingData.booking_number,
      status: bookingData.status || 'pending',
      payment_status: bookingData.payment_status,
      payment_method: bookingData.payment_method,
      travel_date: bookingData.travel_date,
      booking_date: bookingData.booking_date,
      created_at: bookingData.created_at,
      total_price: bookingData.total_amount,
      paid_amount: bookingData.paid_amount,
      num_travelers: (bookingData.adults || 0) + (bookingData.children || 0),
      adults: bookingData.adults || 0,
      children: bookingData.children || 0,
      special_requirements: bookingData.special_requirements,
      // Tour information
      tourId: tourData.id,
      tourTitle: tourData.name,
      tourLocation: tourData.location?.name || tourData.location || 'Various Locations',
      tourDescription: tourData.description,
      tourImage: tourData.featured_image || null,
      // Customer information
      user: bookingData.user || {},
      customer_name: bookingData.user?.name,
      customer_email: bookingData.user?.email,
      customer_phone: bookingData.user?.phone,
      // If customer_details is stored as JSON
      ...(bookingData.customer_details ? JSON.parse(typeof bookingData.customer_details === 'string' ? bookingData.customer_details : JSON.stringify(bookingData.customer_details)) : {})
    };
  };
  
  // Use processed data or fallback
  const bookingData = booking ? processBookingData(booking) : fallbackBooking;
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'Invalid date';
      
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };
  
  // Get status badge based on booking status
  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Confirmed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ExclamationCircleIcon className="h-4 w-4 mr-1" />
            Pending
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-4 w-4 mr-1" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };
  
  // Handle cancel booking
  const handleCancelBooking = () => {
    cancelBookingMutation.mutate();
  };
  
  // Handle download ticket
  const handleDownloadTicket = () => {
    // In a real application, this would generate and download a PDF ticket
    alert('Ticket download functionality would be implemented here.');
  };
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <ErrorMessage 
          message="Failed to load booking details. Please try again later." 
          onRetry={refetch} 
        />
      </div>
    );
  }
  
  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Booking Details</h2>
              <p className="text-gray-600">Booking Reference: {bookingData.booking_number || `BK-${bookingData.id}`}</p>
            </div>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
            {bookingData.status === 'confirmed' && new Date(bookingData.bookingDate) > new Date() && (
              <button
                onClick={() => setShowCancelModal(true)}
                className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
              >
                <XCircleIcon className="h-5 w-5 mr-1" />
                Cancel Booking
              </button>
            )}
            
            {bookingData.status === 'confirmed' && (
              <button
                onClick={handleDownloadTicket}
                className="inline-flex items-center px-4 py-2 border border-primary text-sm font-medium rounded-md text-primary bg-white hover:bg-primary-50"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-1" />
                Download Ticket
              </button>
            )}
            
            <Link
              to="/dashboard/bookings"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Bookings
            </Link>
          </div>
        </div>
        
        <div className="p-6">
          {/* Tour Info */}
          <div className="flex flex-col md:flex-row border border-gray-200 rounded-lg overflow-hidden mb-8">
            <div className="md:w-1/3 h-48 md:h-auto">
              {bookingData.tourImage ? (
                <img
                  src={bookingData.tourImage.startsWith('http') 
                    ? bookingData.tourImage 
                    : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/storage/${bookingData.tourImage}`}
                  alt={bookingData.tourTitle}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.log('Image failed to load:', bookingData.tourImage);
                    e.target.src = '/images/placeholder-tour.jpg';
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-400">No image available</span>
                </div>
              )}
            </div>
            <div className="p-6 md:w-2/3">
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                {bookingData.tourTitle}
              </h3>
              <div className="flex items-center text-gray-500 text-sm mb-4">
                <MapPinIcon className="h-4 w-4 mr-1" />
                <span>{bookingData.tourLocation}</span>
              </div>
              <p className="text-gray-600 mb-4 line-clamp-3">
                {bookingData.tourDescription}
              </p>
              <Link
                to={`/tours/${bookingData.tourId}`}
                className="text-primary hover:text-primary-dark font-medium"
              >
                View Tour Details
              </Link>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Booking Details */}
            <div>
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b border-gray-200">
                Booking Information
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Tour Date</p>
                    <p className="font-medium">{formatDate(bookingData.travel_date || bookingData.booking_date || bookingData.bookingDate)}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <UserGroupIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Number of Travelers</p>
                    <p className="font-medium">
                      {bookingData.num_travelers || bookingData.numTravelers || bookingData.number_of_travelers || 0} 
                      {(bookingData.num_travelers || bookingData.numTravelers || bookingData.number_of_travelers || 0) === 1 ? 'person' : 'people'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <ClockIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Booking Date</p>
                    <p className="font-medium">{formatDate(bookingData.created_at || bookingData.createdAt)}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Total Price</p>
                    <p className="font-medium">${bookingData.total_price || bookingData.totalPrice || bookingData.total_amount || 0}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Payment Status</p>
                    {(() => {
                      const paymentStatus = bookingData.payment_status || bookingData.paymentStatus;
                      const paymentMethod = bookingData.payment_method || bookingData.paymentMethod;
                      
                      if (paymentStatus === 'paid') {
                        return <p className="font-medium text-green-600">Paid</p>;
                      } else if (paymentStatus === 'awaiting_office_payment' || 
                                (paymentStatus === 'pending' && paymentMethod === 'pay_later')) {
                        return (
                          <div>
                            <p className="font-medium text-yellow-600">Pending Payment (Pay Later)</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Please complete your payment at our office or via bank transfer.
                            </p>
                          </div>
                        );
                      } else if (paymentStatus === 'pending') {
                        return <p className="font-medium text-yellow-600">Pending</p>;
                      } else {
                        return <p className="font-medium text-gray-600">{paymentStatus || 'Unknown'}</p>;
                      }
                    })()} 
                  </div>
                </div>
                <div className="flex items-start">
                  <TicketIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Payment Method</p>
                    <p className="font-medium">
                      {(() => {
                        const paymentMethod = bookingData.payment_method || bookingData.paymentMethod;
                        
                        switch(paymentMethod) {
                          case 'credit_card':
                            return 'Credit Card';
                          case 'mpesa':
                            return 'M-Pesa';
                          case 'bank_transfer':
                            return 'Bank Transfer';
                          case 'pay_later':
                            return 'Pay Later';
                          default:
                            return paymentMethod || 'Not specified';
                        }
                      })()}
                    </p>
                    {(bookingData.payment_method || bookingData.paymentMethod) === 'pay_later' && (
                      <div className="mt-2 p-2 bg-blue-50 rounded-md text-xs text-blue-800">
                        <p className="font-medium">Payment Instructions:</p>
                        <ul className="list-disc ml-4 mt-1 space-y-1">
                          <li>Visit our office to make payment in person</li>
                          <li>Or make a bank transfer using the details below</li>
                          <li>Bank: Kenya Commercial Bank</li>
                          <li>Account: KenyaTours-Payments</li>
                          <li>Reference: Booking #{bookingData.id || bookingData.booking_id}</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {bookingData.specialRequirements && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-700 mb-2">Special Requirements</h4>
                  <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                    {bookingData.specialRequirements}
                  </p>
                </div>
              )}
            </div>
            
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b border-gray-200">
                Contact Information
              </h3>
              <div className="space-y-4 mb-6">
                <div className="flex items-start">
                  <UserGroupIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p className="font-medium">
                      {bookingData.contactInfo?.name || 
                       bookingData.contact_name || 
                       bookingData.user?.name || 
                       bookingData.customer_name || 
                       'Not provided'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Email Address</p>
                    <p className="font-medium">
                      {bookingData.contactInfo?.email || 
                       bookingData.contact_email || 
                       bookingData.user?.email || 
                       bookingData.customer_email || 
                       'Not provided'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <PhoneIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Phone Number</p>
                    <p className="font-medium">
                      {bookingData.contactInfo?.phone || 
                       bookingData.contact_phone || 
                       bookingData.user?.phone || 
                       bookingData.phone_number || 
                       bookingData.customer_phone || 
                       'Not provided'}
                    </p>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b border-gray-200">
                Need Help?
              </h3>
              <p className="text-gray-600 mb-4">
                If you have any questions or need to make changes to your booking, please contact our customer support team.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium mb-1">Customer Support</p>
                <p className="text-gray-600 mb-1">Email: support@kenyatours.com</p>
                <p className="text-gray-600">Phone: +254 123 456 789</p>
              </div>
            </div>
          </div>
          
          {/* Itinerary */}
          {bookingData.itinerary && bookingData.itinerary.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b border-gray-200">
                Tour Itinerary
              </h3>
              <div className="space-y-6">
                {bookingData.itinerary.map((day) => (
                <div key={day.day} className="flex">
                  <div className="mr-4 flex flex-col items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-50 flex items-center justify-center border-2 border-primary">
                      <span className="text-primary font-semibold">{day.day}</span>
                    </div>
                    {day.day < bookingData.itinerary.length && (
                      <div className="h-full w-0.5 bg-gray-200 mt-2"></div>
                    )}
                  </div>
                  <div className="pb-6">
                    <h4 className="font-medium text-gray-800 mb-1">{day.title}</h4>
                    <p className="text-gray-600">{day.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
      
      {/* Cancel Booking Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 rounded-full p-3">
                <XCircleIcon className="h-10 w-10 text-red-500" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-center mb-2">Cancel Booking</h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to cancel this booking? Please review our cancellation policy below.
            </p>
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <h4 className="font-medium mb-2">Cancellation Policy</h4>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Free cancellation up to 7 days before the tour date</li>
                <li>50% fee for cancellations 3-7 days before the tour</li>
                <li>No refund for cancellations less than 3 days before the tour</li>
              </ul>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Keep Booking
              </button>
              <button
                onClick={handleCancelBooking}
                disabled={cancelBookingMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                {cancelBookingMutation.isLoading ? 'Processing...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingDetail;
